/**
 * Customer Videos Component
 * Handles inline video playback with custom controls and horizontal scrolling
 */

class CustomerVideos {
	constructor(container) {
		this.container = container;
		this.blockId = container.dataset.blockId;
		this.videoItems = Array.from(container.querySelectorAll('.customer-videos__item'));
		this.grid = container.querySelector('.customer-videos__grid');
		this.scrollbar = container.querySelector(`.customer-videos__scrollbar[data-scrollbar-id="${this.blockId}"]`);
		this.scrollbarProgress = this.scrollbar?.querySelector('.customer-videos__scrollbar-progress');
		this.isDragging = false;

		this.isGridDragging = false;
		this.gridDragStartX = 0;
		this.gridDragStartScrollLeft = 0;
		this.gridDidDrag = false;
		this.gridPointerId = null;
		this.activeVideoIndex = null;
		this.controlsHideTimeout = null;
		this.init();
	}

	init() {
		this.setupEventListeners();
		if (this.grid && this.scrollbar) {
			this.setupScrollbar();
		}

		// Ensure all videos are paused initially (no autoplay).
		this.pauseAllVideos({ reset: false });

		// Preload videos when they come into view (reduces delay on iOS Safari)
		this.setupVideoPreloading();

		// Close kebab menu when clicking outside
		document.addEventListener('click', (e) => {
			if (!e.target.closest('.customer-videos__kebab-wrapper')) {
				this.closeAllKebabMenus();
			}
		});

		// Close kebab menu on window scroll (Safari fix for fixed positioning)
		window.addEventListener('scroll', () => {
			this.closeAllKebabMenus();
		}, { passive: true });

		// Close kebab menu on touch move to prevent menu drift in Safari
		this.container.addEventListener('touchmove', () => {
			this.closeAllKebabMenus();
		}, { passive: true });
	}

	setupVideoPreloading() {
		// Use Intersection Observer to preload videos when they come into view
		// This reduces the delay when clicking play on iOS Safari
		if (!('IntersectionObserver' in window)) return;

		const observer = new IntersectionObserver((entries) => {
			entries.forEach((entry) => {
				if (entry.isIntersecting) {
					const video = entry.target.querySelector('video');
					if (video && video.preload !== 'auto') {
						video.preload = 'auto';
						video.load();
					}
					// Stop observing once preloaded
					observer.unobserve(entry.target);
				}
			});
		}, {
			rootMargin: '100px', // Start preloading 100px before coming into view
			threshold: 0
		});

		// Observe all video items
		this.videoItems.forEach((item) => {
			observer.observe(item);
		});
	}

	setupScrollbar() {
		// Update scrollbar on scroll
		this.grid.addEventListener('scroll', () => {
			this.updateScrollbar();
			// Close kebab menu on scroll to prevent positioning issues in Safari
			this.closeAllKebabMenus();
		});

		// Update scrollbar on resize
		window.addEventListener('resize', () => {
			this.updateScrollbar();
		});

		// Initial update
		this.updateScrollbar();

		// Click on scrollbar to jump to position
		this.scrollbar.addEventListener('click', (e) => {
			if (!this.isDragging) {
				this.handleScrollbarClick(e);
			}
		});

		// Drag scrollbar
		this.scrollbar.addEventListener('mousedown', (e) => {
			this.startDrag(e);
		});

		document.addEventListener('mousemove', (e) => {
			if (this.isDragging) {
				this.handleDrag(e);
			}
		});

		document.addEventListener('mouseup', () => {
			this.isDragging = false;
		});

		// Touch support
		this.scrollbar.addEventListener('touchstart', (e) => {
			this.startDrag(e);
		});

		document.addEventListener('touchmove', (e) => {
			if (this.isDragging) {
				this.handleDrag(e);
			}
		});

		document.addEventListener('touchend', () => {
			this.isDragging = false;
		});
	}

	updateScrollbar() {
		if (!this.grid || !this.scrollbarProgress || !this.scrollbar) return;

		const scrollLeft = this.grid.scrollLeft;
		const scrollWidth = this.grid.scrollWidth;
		const clientWidth = this.grid.clientWidth;
		const maxScroll = scrollWidth - clientWidth;

		if (maxScroll <= 0) {
			this.scrollbarProgress.style.width = '100%';
			this.scrollbarProgress.style.left = '0%';
			return;
		}

		// Calculate thumb size: proportion of visible content to total content
		const thumbWidthPercentage = (clientWidth / scrollWidth) * 100;
		
		// Calculate thumb position: how far we've scrolled relative to max scroll
		// Position is calculated as percentage of the available track (100% - thumb width)
		const scrollPercentage = scrollLeft / maxScroll;
		const availableTrack = 100 - thumbWidthPercentage;
		const thumbPosition = scrollPercentage * availableTrack;

		// Set thumb width and position
		this.scrollbarProgress.style.width = `${thumbWidthPercentage}%`;
		this.scrollbarProgress.style.left = `${thumbPosition}%`;
	}

	handleScrollbarClick(e) {
		if (!this.grid || !this.scrollbar) return;

		const rect = this.scrollbar.getBoundingClientRect();
		const clickX = e.clientX - rect.left;
		const scrollbarWidth = rect.width;
		
		// Don't scroll if clicking directly on the thumb
		const thumbRect = this.scrollbarProgress.getBoundingClientRect();
		if (clickX >= thumbRect.left - rect.left && clickX <= thumbRect.right - rect.left) {
			return;
		}

		// Calculate scroll position based on click position
		const scrollWidth = this.grid.scrollWidth;
		const clientWidth = this.grid.clientWidth;
		const maxScroll = scrollWidth - clientWidth;
		const thumbWidth = (clientWidth / scrollWidth) * scrollbarWidth;
		const availableTrack = scrollbarWidth - thumbWidth;
		
		// Calculate which position to scroll to
		const clickPercentage = clickX / scrollbarWidth;
		const scrollPercentage = clickPercentage;
		const targetScroll = scrollPercentage * maxScroll;

		this.grid.scrollTo({
			left: targetScroll,
			behavior: 'smooth'
		});
	}

	startDrag(e) {
		this.isDragging = true;
		e.preventDefault();
	}

	handleDrag(e) {
		if (!this.grid || !this.scrollbar) return;

		const rect = this.scrollbar.getBoundingClientRect();
		const clientX = e.touches ? e.touches[0].clientX : e.clientX;
		const dragX = clientX - rect.left;
		const scrollbarWidth = rect.width;
		
		const scrollWidth = this.grid.scrollWidth;
		const clientWidth = this.grid.clientWidth;
		const maxScroll = scrollWidth - clientWidth;
		const thumbWidth = (clientWidth / scrollWidth) * scrollbarWidth;
		const availableTrack = scrollbarWidth - thumbWidth;
		
		// Calculate scroll position based on drag position
		// Clamp dragX to available track area
		const clampedDragX = Math.max(0, Math.min(availableTrack, dragX - (thumbWidth / 2)));
		const scrollPercentage = clampedDragX / availableTrack;

		this.grid.scrollLeft = scrollPercentage * maxScroll;
	}

	setupEventListeners() {
		this.setupGridDrag();

		this.videoItems.forEach((item, index) => {
			const playButton = item.querySelector('.customer-videos__play-button');
			const video = item.querySelector('video');

			if (!video) return;

			// Never enable native controls - we use custom controls only.
			video.controls = false;
			video.muted = false;
			video.defaultMuted = false;

			// Clicking directly on the video toggles play/pause when playing
			// Note: In fullscreen mode on mobile, video has pointer-events: none via CSS
			// so clicks go to the poster/item instead
			video.addEventListener('click', (e) => {
				e.preventDefault();
				e.stopPropagation();
				if (this.gridDidDrag) return;

				// If video is playing, toggle play/pause
				if (item.classList.contains('has-controls')) {
					this.togglePlayPause(video, item);
				} else {
					// Start playback if not yet playing
					this.playInline(index);
				}
			});

			// Handle clicks on poster (for mobile fullscreen where video has pointer-events: none)
			const poster = item.querySelector('.customer-videos__item-poster');
			if (poster) {
				poster.addEventListener('click', (e) => {
					// Don't handle if click was on video, play button, or controls
					if (e.target === video) return;
					if (e.target?.closest?.('.customer-videos__play-button')) return;
					if (e.target?.closest?.('.customer-videos__controls')) return;
					if (this.gridDidDrag) return;

					e.stopPropagation();

					// If video is playing, toggle play/pause
					if (item.classList.contains('has-controls')) {
						this.togglePlayPause(video, item);
					}

					else {
						// Start playback if not yet playing
						this.playInline(index);
					}
				});
			}

			// Clicking the play button starts inline playback.
			if (playButton) {
				playButton.addEventListener('click', (e) => {
					e.preventDefault();
					e.stopPropagation();
					if (this.gridDidDrag) return;
					this.playInline(index);
				});
			}

			// Allow clicking the video area to start/pause playback
			item.addEventListener('click', (e) => {
				if (e.target?.closest?.('.customer-videos__play-button')) return;
				if (e.target?.closest?.('.customer-videos__controls')) return;
				if (this.gridDidDrag) return;

				// If video is already playing (has controls), toggle play/pause
				if (item.classList.contains('has-controls')) {
					this.togglePlayPause(video, item);
					return;
				}

				// Otherwise start playback
				this.playInline(index);
			});

			// Setup custom controls for this video item
			this.setupCustomControls(item, index, video);

			// Video event listeners for syncing state
			video.addEventListener('pause', () => this.updatePlayState(item, false));
			video.addEventListener('ended', () => this.handleVideoEnded(item, index));
			
			// Use timeupdate to detect when video is actually rendering (fires on first frame)
			// This is faster than 'playing' event and prevents black flash on iOS Safari
			video.addEventListener('timeupdate', () => {
				this.updateProgress(item, video);
				// Add is-playing class as soon as video starts rendering frames
				if (!video.paused && video.currentTime > 0) {
					this.updatePlayState(item, true);
				}
			});
			video.addEventListener('volumechange', () => this.updateMuteState(item, video));

			// Show/hide controls on hover/touch
			item.addEventListener('mouseenter', () => this.showControls(item));
			item.addEventListener('mouseleave', () => this.scheduleHideControls(item));
			item.addEventListener('touchstart', () => this.showControls(item), { passive: true });
		});
	}

	setupCustomControls(item, index, video) {
		const controls = item.querySelector('.customer-videos__controls');
		if (!controls) return;

		// Play/Pause button
		const playPauseBtn = controls.querySelector('[data-action="play-pause"]');
		if (playPauseBtn) {
			playPauseBtn.addEventListener('click', (e) => {
				e.stopPropagation();
				this.togglePlayPause(video, item);
			});
		}

		// Mute button (inside kebab menu)
		const muteBtn = controls.querySelector('.customer-videos__mute-btn');
		if (muteBtn) {
			muteBtn.addEventListener('click', (e) => {
				e.stopPropagation();
				this.toggleMute(video, item);
				// Don't close menu so user can see state change
			});
		}

		// Progress bar
		const progressBar = controls.querySelector('.customer-videos__progress');
		if (progressBar) {
			progressBar.addEventListener('click', (e) => {
				e.stopPropagation();
				this.seekVideo(e, video, progressBar);
			});
		}

		// Kebab menu button
		const kebabBtn = controls.querySelector('[data-action="menu"]');
		const kebabMenu = controls.querySelector('.customer-videos__kebab-menu');
		if (kebabBtn && kebabMenu) {
			kebabBtn.addEventListener('click', (e) => {
				e.stopPropagation();
				this.toggleKebabMenu(kebabBtn, kebabMenu);
			});
		}

		// Speed options
		const speedBtns = controls.querySelectorAll('[data-speed]');
		speedBtns.forEach((btn) => {
			btn.addEventListener('click', (e) => {
				e.stopPropagation();
				const speed = parseFloat(btn.dataset.speed);
				this.setPlaybackSpeed(video, speed, controls);
			});
		});

		// Fullscreen button
		const fullscreenBtn = controls.querySelector('[data-action="fullscreen"]');
		if (fullscreenBtn) {
			fullscreenBtn.addEventListener('click', (e) => {
				e.stopPropagation();
				this.toggleFullscreen(video, item);
				this.closeAllKebabMenus();
			});
		}

		// Keep controls visible when interacting with menu
		controls.addEventListener('click', (e) => {
			e.stopPropagation();
			this.showControls(item);
		});
	}

	togglePlayPause(video, item) {
		if (video.paused) {
			video.play().catch(() => {});
		}

		else {
			video.pause();
		}
	}

	updatePlayState(item, isPlaying) {
		if (isPlaying) {
			item.classList.add('is-playing');
		}

		else {
			item.classList.remove('is-playing');
		}
	}

	toggleMute(video, item) {
		video.muted = !video.muted;

		// Immediately update mute button class (for when menu is in body - Safari fix)
		const muteBtn = item.querySelector('.customer-videos__mute-btn');
		if (muteBtn) {
			muteBtn.classList.toggle('is-muted', video.muted);
		}

		// Also update any open menu in body
		document.querySelectorAll('.customer-videos__kebab-menu.is-open .customer-videos__mute-btn').forEach((btn) => {
			btn.classList.toggle('is-muted', video.muted);
		});
	}

	updateMuteState(item, video) {
		// Update class on item
		if (video.muted) {
			item.classList.add('is-muted');
		} else {
			item.classList.remove('is-muted');
		}

		// Also update class on mute button directly (for when menu is in body - Safari fix)
		const muteBtn = item.querySelector('.customer-videos__mute-btn');
		if (muteBtn) {
			muteBtn.classList.toggle('is-muted', video.muted);
		}

		// Update any open menu in body that belongs to this item
		document.querySelectorAll('.customer-videos__kebab-menu.is-open .customer-videos__mute-btn').forEach((btn) => {
			btn.classList.toggle('is-muted', video.muted);
		});
	}

	updateProgress(item, video) {
		const progressFill = item.querySelector('.customer-videos__progress-fill');
		if (!progressFill || !video.duration) return;

		const percentage = (video.currentTime / video.duration) * 100;
		progressFill.style.width = `${percentage}%`;
	}

	seekVideo(e, video, progressBar) {
		const rect = progressBar.getBoundingClientRect();
		const clickX = e.clientX - rect.left;
		const percentage = clickX / rect.width;
		video.currentTime = percentage * video.duration;
	}

	setPlaybackSpeed(video, speed, controls) {
		video.playbackRate = speed;

		// Update active state on speed menu items
		const speedBtns = controls.querySelectorAll('[data-speed]');
		speedBtns.forEach((btn) => {
			btn.classList.toggle('is-active', parseFloat(btn.dataset.speed) === speed);
		});

		// Close the kebab menu after selecting speed
		this.closeAllKebabMenus();
	}

	toggleKebabMenu(btn, menu) {
		const isOpen = menu.classList.contains('is-open');

		// Close all other menus first
		this.closeAllKebabMenus();

		if (!isOpen) {
			// Check if we're in fullscreen mode
			const isFullscreen = document.fullscreenElement || document.webkitFullscreenElement;

			// Store original parent for later restoration (Safari fix)
			if (!menu._originalParent) {
				menu._originalParent = menu.parentElement;
				menu._originalNextSibling = menu.nextSibling;
			}

			// Only move menu to body if NOT in fullscreen mode
			// In fullscreen, the menu must stay within the fullscreen element to be visible
			if (!isFullscreen) {
				document.body.appendChild(menu);
			}

			// Reset scroll position
			menu.scrollTop = 0;

			// Get button position
			const btnRect = btn.getBoundingClientRect();

			// Temporarily show to measure
			menu.style.visibility = 'hidden';
			menu.style.opacity = '1';
			menu.style.pointerEvents = 'none';
			menu.classList.add('is-open');

			const actualHeight = menu.offsetHeight;
			const actualWidth = menu.offsetWidth || 150;

			const viewportHeight = window.innerHeight;
			const viewportWidth = window.innerWidth;

			// Calculate position - prefer opening upward
			let top = btnRect.top - actualHeight - 8;
			let left = btnRect.right - actualWidth;

			// If not enough space above, open below
			if (top < 10) {
				top = btnRect.bottom + 8;
			}

			// Ensure menu doesn't go off bottom of viewport
			if (top + actualHeight > viewportHeight - 10) {
				top = viewportHeight - actualHeight - 10;
			}

			// Keep within viewport horizontally
			if (left < 10) {
				left = 10;
			}
			if (left + actualWidth > viewportWidth - 10) {
				left = viewportWidth - actualWidth - 10;
			}

			// Apply final position and show
			// In fullscreen mode, use fixed positioning relative to the fullscreen element
			if (isFullscreen) {
				menu.style.position = 'fixed';
			}
			menu.style.top = `${top}px`;
			menu.style.left = `${left}px`;
			menu.style.visibility = '';
			menu.style.opacity = '';
			menu.style.pointerEvents = '';

			btn.setAttribute('aria-expanded', 'true');

			// Store reference to button for this menu
			menu._triggerBtn = btn;

			// Ensure scroll is at top after opening (Safari fix)
			requestAnimationFrame(() => {
				menu.scrollTop = 0;
			});
		}
	}

	closeAllKebabMenus() {
		// Find all open menus in body and close them
		document.querySelectorAll('.customer-videos__kebab-menu.is-open').forEach((menu) => {
			menu.classList.remove('is-open');

			// Restore menu to original parent if it was moved
			if (menu._originalParent && menu.parentElement === document.body) {
				if (menu._originalNextSibling) {
					menu._originalParent.insertBefore(menu, menu._originalNextSibling);
				}

				else {
					menu._originalParent.appendChild(menu);
				}
			}

			if (menu._triggerBtn) {
				menu._triggerBtn.setAttribute('aria-expanded', 'false');
				menu._triggerBtn = null;
			}
		});

		// Also handle any menus still in the video items
		this.videoItems.forEach((item) => {
			const menu = item.querySelector('.customer-videos__kebab-menu');
			const btn = item.querySelector('[data-action="menu"]');
			if (menu) menu.classList.remove('is-open');
			if (btn) btn.setAttribute('aria-expanded', 'false');
		});
	}

	toggleFullscreen(video, item) {
		// Close kebab menu first
		this.closeAllKebabMenus();

		// Helper to clean up fullscreen state
		const cleanupFullscreen = () => {
			video.controls = false;
			item.classList.remove('is-fullscreen');
			// Reset any inline styles that might have been added
			item.style.cssText = '';
		};

		if (document.fullscreenElement || document.webkitFullscreenElement) {
			// Exit fullscreen - clean up first
			cleanupFullscreen();
			if (document.exitFullscreen) {
				document.exitFullscreen().catch(() => {});
			} else if (document.webkitExitFullscreen) {
				document.webkitExitFullscreen();
			}
		} else {
			// Detect if iOS Safari (uses native fullscreen with built-in controls)
			const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

			const enterFullscreen = () => {
				if (video.webkitEnterFullscreen && isIOS) {
					// iOS Safari - uses native fullscreen with built-in controls
					video.controls = true;
					video.webkitEnterFullscreen();
					return Promise.resolve();
				} else if (item.requestFullscreen) {
					// Fullscreen the container (item) so custom controls remain visible
					return item.requestFullscreen();
				} else if (item.webkitRequestFullscreen) {
					// Safari desktop - fullscreen the container
					return item.webkitRequestFullscreen();
				}
				return Promise.reject();
			};

			// Add fullscreen class for styling
			item.classList.add('is-fullscreen');

			enterFullscreen().then(() => {
				// Listen for fullscreen exit
				const onFullscreenChange = () => {
					const isFullscreen = document.fullscreenElement || document.webkitFullscreenElement;
					if (!isFullscreen) {
						cleanupFullscreen();
						document.removeEventListener('fullscreenchange', onFullscreenChange);
						document.removeEventListener('webkitfullscreenchange', onFullscreenChange);
					}
				};
				document.addEventListener('fullscreenchange', onFullscreenChange);
				document.addEventListener('webkitfullscreenchange', onFullscreenChange);

				// For iOS Safari native fullscreen, listen for webkitendfullscreen event
				if (isIOS) {
					video.addEventListener('webkitendfullscreen', () => {
						cleanupFullscreen();
					}, { once: true });
				}
			}).catch(() => {
				cleanupFullscreen();
			});
		}
	}

	handleVideoEnded(item, index) {
		item.classList.remove('is-playing');
		// Optionally reset to beginning
		const video = item.querySelector('video');
		if (video) {
			video.currentTime = 0;
		}
	}

	showControls(item) {
		if (this.controlsHideTimeout) {
			clearTimeout(this.controlsHideTimeout);
			this.controlsHideTimeout = null;
		}
		item.classList.add('controls-visible');
	}

	scheduleHideControls(item) {
		if (this.controlsHideTimeout) {
			clearTimeout(this.controlsHideTimeout);
		}
		this.controlsHideTimeout = setTimeout(() => {
			item.classList.remove('controls-visible');
			this.closeAllKebabMenus();
		}, 2000);
	}

	setupGridDrag() {
		if (!this.grid) return;

		const dragThresholdPx = 6;

		this.grid.addEventListener('pointerdown', (e) => {
			// Only handle primary pointer.
			if (e.button != null && e.button !== 0) return;
			if (e.target?.closest?.('.customer-videos__play-button')) return;
			if (e.target?.closest?.('.customer-videos__controls')) return;
			if (e.target?.closest?.('.customer-videos__kebab-menu')) return;
			if (e.target?.closest?.('video')) return;

			this.isGridDragging = true;
			this.gridDidDrag = false;
			this.gridPointerId = e.pointerId;
			this.gridDragStartX = e.clientX;
			this.gridDragStartScrollLeft = this.grid.scrollLeft;

			this.grid.classList.add('is-dragging');
			try {
				this.grid.setPointerCapture(e.pointerId);
			} catch (err) {
				// Some environments may not support pointer capture.
			}
		});

		this.grid.addEventListener('pointermove', (e) => {
			if (!this.isGridDragging) return;
			if (this.gridPointerId != null && e.pointerId !== this.gridPointerId) return;

			const deltaX = e.clientX - this.gridDragStartX;
			if (!this.gridDidDrag && Math.abs(deltaX) >= dragThresholdPx) {
				this.gridDidDrag = true;
			}

			if (this.gridDidDrag) {
				// Invert drag direction to match “grab and drag” expectation.
				this.grid.scrollLeft = this.gridDragStartScrollLeft - deltaX;
			}
		});

		const endDrag = (e) => {
			if (!this.isGridDragging) return;
			if (this.gridPointerId != null && e.pointerId !== this.gridPointerId) return;

			this.isGridDragging = false;
			this.gridPointerId = null;
			this.grid.classList.remove('is-dragging');

			// Reset the “did drag” flag shortly after, so clicks still work.
			window.setTimeout(() => {
				this.gridDidDrag = false;
			}, 0);
		};

		this.grid.addEventListener('pointerup', endDrag);
		this.grid.addEventListener('pointercancel', endDrag);
		this.grid.addEventListener('pointerleave', endDrag);
	}

	playInline(videoIndex) {
		const item = this.videoItems[videoIndex];
		const video = item?.querySelector('video');
		if (!item || !video) return;

		// Pause and reset other videos back to initial state.
		this.videoItems.forEach((otherItem, otherIndex) => {
			if (otherIndex === videoIndex) return;
			const otherVideo = otherItem.querySelector('video');
			if (!otherVideo) return;

			otherVideo.pause();
			otherItem.classList.remove('has-controls', 'is-playing', 'is-muted', 'controls-visible');

			try {
				otherVideo.currentTime = 0;
			} catch (err) {
				// Some browsers may block setting currentTime until metadata is loaded.
			}
		});

		// Show custom controls and play inline (never use native controls).
		this.activeVideoIndex = videoIndex;
		item.classList.add('has-controls');
		this.showControls(item);

		// Update mute state visual
		if (video.muted) {
			item.classList.add('is-muted');
		}

		video.play().catch(() => {});
	}

	pauseAllVideos({ reset = true } = {}) {
		this.activeVideoIndex = null;
		this.videoItems.forEach((item) => {
			const video = item.querySelector('video');
			if (!video) return;

			video.pause();
			item.classList.remove('has-controls', 'is-playing', 'is-muted', 'controls-visible');
			this.closeAllKebabMenus();

			if (!reset) return;
			try {
				video.currentTime = 0;
			} catch (err) {
				// Some browsers may block setting currentTime until metadata is loaded.
			}
		});
	}
}

// Initialize all customer video galleries on the page
const initCustomerVideos = () => {
	const videoContainers = document.querySelectorAll('.customer-videos[data-block-id]');
	videoContainers.forEach(container => {
		if (container.dataset.customerVideosInitialized === 'true') return;

		container.dataset.customerVideosInitialized = 'true';
		new CustomerVideos(container);
	});
};

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', initCustomerVideos, { once: true });
} else {
	initCustomerVideos();
}

// Export for potential module use
if (typeof module !== 'undefined' && module.exports) {
	module.exports = CustomerVideos;
}

